import UancEvent from '../../services/UancEvent';
import {
    AD_CONSOLE_STORE,
    ADS_LOADED_EVENT,
    STATE_SERVICE,
    TEST_CONFIG_API,
    TEST_CONFIG_API_ITG,
    UANC_CONFIG_EVENT,
} from '../../configs/constants';
import { IConfig, ITestConfig } from '../../uanc.types';
import { fetchData } from '../../services/fetch-data';
import { DEFAULT_CONFIG } from '../../configs/default-configs/config';
import { embedPlugins } from '../../services/plugins/plugins';
import DebugUANC from '../version/debug';
import { getParamURL, isEmpty, isStringType } from '../../helpers/js-utils';
import { configService } from '../../services/config-service/config-service';
import { AB_POPUP_EXT_SERVICES_NAMES, AB_POPUP_SERVICES_ID } from '../../services/adblock-popup/adblock-popup.config';
import { setGlobalAdServices } from '../../services/ad-services/ad-services';
import { getDetection } from '../../services/detection';
import { handleSlotRenderEnded, handleSlotRequested } from '../../services/ad-services/DFP/embedding-dfp.utils';

/**
 * Czyszczenie prebida.
 * Reset stanu, kasowanie aukcji z pamięci i jednostek.
 *
 * @returns {Promise<void>}
 */
const resetPrebid = (): Promise<void> => {
    const { UANC, state } = window.Inpl.Ad;

    return new Promise((resolve) => {
        if (state?.isPrebidReady && window.pbjs?.que) {
            UANC?.setState({ isPrebidReady: false });
            window.pbjs?.que.push(() => {
                window.pbjs?.clearAllAuctions();
                window.pbjs?.removeAdUnit();
                resolve();
            });
        } else {
            resolve();
        }
    });
};

/**
 * Restart Ado.
 * Kasowanie załadowanego ado, ustawienie początkowych stanów.
 *
 * @returns {Promise<void>}
 */
const resetAOService = (): Promise<void> => {
    const { Ad } = window.Inpl;
    const { UANC, services } = Ad;

    return new Promise((resolve) => {
        // czyszczenie AO
        if (window.ado) {
            delete window.ado;
            delete window.adocf;
        }

        // ustawiane początkowe stany serwisu
        UANC?.setState({ ADOCEANServiceState: STATE_SERVICE.PENDING });
        UANC?.setState({ isAdoScriptReady: false });

        if (services && 'ADOCEANService' in services) {
            delete services.ADOCEANService;
        }
        resolve();
    });
};

/**
 * Restart DFP.
 * Usuwa dotychczasowe jednostki reklamowe. Czyści targetowania GPT. Ustawia początkowe stany.
 *
 * @returns {void}
 */
const resetDFPService = async (): Promise<void> => {
    const { Ad } = window.Inpl;
    const { UANC, services, event: uancEvent } = Ad;

    return new Promise((resolve) => {
        // usunięcie nasłuchiwań na poprzednie eventy
        uancEvent?.remove('slotRequested', googletag.pubads(), handleSlotRequested);
        uancEvent?.remove('slotRenderEnded', googletag.pubads(), handleSlotRenderEnded);

        // ustawiane początkowe stany serwisu
        UANC?.setState({ DFPServiceState: STATE_SERVICE.PENDING });
        UANC?.setState({ isGoogleScriptReady: false });
        if (services && 'DFPService' in services) {
            delete services.DFPService;
        }

        // usuwanie aktywnych jednostek reklamowych + czyszczenie targetingu GPT
        if (googletag?.cmd && googletag?.pubads) {
            googletag.cmd.push(() => {
                googletag.destroySlots();
                googletag.pubads().clearTargeting();
                resolve();
            });
        } else resolve();
    });
};

/**
 * Wywołuje restarty konkretnych usług. "Zeruje" stan services.
 *
 * @returns {Promise<void>}
 */
const resetServices = async (): Promise<void> => {
    const { UANC } = window.Inpl.Ad;
    const promisesReset = [resetPrebid, resetAOService, resetDFPService];
    await Promise.all(promisesReset.map((serviceReset) => serviceReset()));

    UANC!.unresolvedServices.length = 0;
    // usługi wyzerowane, ustawienie stanu "początkowego"
    window.Inpl.Ad.services = {
        resolved: false,
    };
};

/**
 * Przeładowanie wstawki.
 * W pierwszej kolejności ustawia services.resolved w stanie niegotowości, aby serwisy nie wywoływały metod osadzania reklam.
 * Ponownie ustawia detection, settings. Pobiera konfigurację z ADC. Obsługa noAdvertisement.
 * Reset Prebid - czyszczenie aukcji i cache - pozbycie się informacji o bids.
 * Reset adserwerów - ustawienie stanów początkowych serwisów, usuwanie jednostek reklamowych.
 * Ładowanie loadApp - standardowe operacje podczas uruchamiania wstawki, m.in. testyAB, ładowanie pluginów, serwisów i ich skryptów.
 *
 * @returns {void}
 */
export const reloadUanc = async () => {
    console.log('--- reloadUanc ---');
    // Ustawienie jedynie resolved, aby serwisy wiedziały o niedostępności usług. Istniejące usługi potrzebne do resetowania.
    window.Inpl.Ad.services = {
        ...window.Inpl.Ad.services,
        resolved: false,
    };

    await resetServices();

    const { Ad } = window.Inpl;
    // ustawienie statusu o odświeżaniu wstawki
    Ad.UANC?.setState({ isReloadUanc: true });
    Ad.plugins?.AdConsole?.isEnabled && Ad.UANC?.setState({ reloadAdConsole: true });

    (Ad.clearAds || []).forEach((item: any) => {
        if (typeof item === 'function') item();
    });
    Ad.clearAds = [];
    Ad.detection = getDetection();

    window.Inpl.Ad.exSettings && (window.Inpl.Ad.exSettings = {});
    await prepareSettings();

    Ad.host = getHostForConfig();
    Ad.config = await adcRequest();

    if (Ad.settings?.noAdvertisement) {
        noAd();
    } else {
        loadApp();
    }
};

/**
 * Nasłuchuje na zmianę URL / przejście na podstronach SPA, bez przeładowania.
 * Po wykryciu zmiany ścieżki, uruchamia proces przeładowania wstawki.
 * Popstate - wykrycie przejścia za pośrednictwem wstecz / dalej
 * History - wykrycie zmiany URL - wpięcie i nadpisanie pushState
 *
 * @returns {void}
 */
export const detectRouteChange = (): void => {
    const getLocation = (): string => location.origin + location.pathname + location.search;
    let currentLocation: string = getLocation();

    // wykrywanie zmiany URL - history
    (function (history: History) {
        const originalPushState = history.pushState;

        history.pushState = function (state: any, unused: string, url?: string | URL) {
            originalPushState.apply(history, [state, unused, url]);
            const newLocation = getLocation();
            if (currentLocation !== newLocation) {
                reloadUanc();
                currentLocation = newLocation;
            }
        };
    })(window.history);

    // wykrywanie przejścia - wstecz / dalej
    window.addEventListener('popstate', () => {
        if (currentLocation !== getLocation()) {
            reloadUanc();
            currentLocation = getLocation();
        }
    });
};

/**
 * Określa czy adblock popup powinien zostać zainicjowany.
 *
 * @returns {boolean} informacja czy inicjować adblock popup
 */
export const isAdblockPopupInit = (): boolean => {
    const { Ad } = window.Inpl;

    return (
        !Ad.detection?.isMobile &&
        !!(Ad.settings?.serviceId
            ? AB_POPUP_SERVICES_ID.find((item) => item === Ad.settings?.serviceId)
            : AB_POPUP_EXT_SERVICES_NAMES.find((item) => item === location.hostname))
    );
};

/**
 * Zwraca informacje czy UANC ma działać w wersji DEBUG.
 * Określane na podstawie parametrów/hash URL lub czy consola uanc jest uruchomiona
 *
 * @returns {boolean} informacja czy działać w wersji debug
 */
const isDebugMode = (): boolean => {
    const url = window.location.href;
    const isConsoleOpen = !!localStorage.getItem(AD_CONSOLE_STORE);
    return (
        url.match(/\?uanc|#inpltester2ad|adtester2023|vuanc/gim) !== null ||
        url.match(/[?&]inpltestuanc=[^&]+/gim) !== null ||
        isConsoleOpen
    );
};

/**
 * Zwraca informacje jaka konfiguracja ma zostać przetestowana.
 * Tester konfigów ustawiony w AdSet, pobrany z ADC
 *
 * @returns {ITestConfig} informacje o teście
 */
const getTestConfig = (): ITestConfig => {
    const url = new URL(location.href);
    return {
        isTestConfig: true,
        testConfigId: url.searchParams.get('inpltestuanc'),
        testConfigIdTime: url.searchParams.get('time'),
    };
};

/**
 * Ustala wersję UANCA.
 * Wersja debug rozszerza UANCA o logger
 *
 * @returns {Promise<void>}
 */
export const setUANCVersion = async (): Promise<void> => {
    const { Ad } = window.Inpl;

    if (!Ad.event) {
        Ad.event = new UancEvent();
    }
    if (isDebugMode()) {
        const debugUANC = await import(/* webpackChunkName: "debug-version" */ '../../core/version/debug');
        window.Inpl.Ad.UANC = new debugUANC.default();

        if (window.location.href.match(/[?&]inpltestuanc=[^&]+/gim) !== null) {
            Ad.testConfig = getTestConfig();
        }

        Ad.event.custom('Inpl.Uanc.Instantiated');
    } else {
        const proxyUANC = await import(/* webpackChunkName: "proxy-version" */ '../../core/version/proxy');
        window.Inpl.Ad.UANC = new proxyUANC.default().init;

        Ad.event.custom('Inpl.Uanc.Instantiated');
    }
};

/**
 * Ustala host, dla którego UANC wykona zapytanie do ADC
 * Istnieje możliwość nadpisania całej ścieżki lub samej domeny w zależności od kilku paramterów.
 * exSettings.domain | testconfig | uanc_url
 *
 * @returns {string} adres hosta
 */
export const getHostForConfig = (): string => {
    const { Ad } = window.Inpl;

    const pathName = Ad.exSettings?.pathPrefix ? '/' + Ad.exSettings.pathPrefix + location.pathname : location.pathname;
    let host = location.host + pathName.replace(/^\/+/, '/');

    if (Ad.exSettings?.domain) host = Ad.exSettings.domain.trim() + location.pathname;
    host = host.endsWith('/') ? host.slice(0, -1) : host;

    if (location.search) {
        const urlToChange = getParamURL(location.href, 'uanc_url');
        if (urlToChange) host = urlToChange;

        const isTestConfig = getParamURL(location.href, 'testconfig');
        if (isTestConfig) host = `${host}?testconfig=${isTestConfig}`;
    }

    return host;
};

/**
 * Ustala środowisko na jakim działa dany serwis i na jakim mają być osadzone reklamy.
 *
 * @param {string} host - nazwa hosta
 * @returns {string} nazwę środowiska
 */
export const getEnvironment = (host: string): string => {
    if (host.endsWith('.itg.inpl.work')) {
        return 'integration';
    } else if (/^localhost(:\d+)?$/.test(host)) {
        return 'development';
    } else if (host.endsWith('.ds.inpl.work')) {
        return 'development';
    } else return 'production';
};

/**
 * Przygotowuje URL pod jaki ma być wykonywany request do ADC.
 *
 * @param {string} host - nazwa hosta
 * @param {ITestConfig | null} testConfig - informacje o teście jeżeli istnieje
 * @returns {string} adres zapytania
 */
export const prepareAdcURL = (host: string, testConfig: ITestConfig | null): string => {
    const isITG = ['integration', 'development'].indexOf(getEnvironment(host)) !== -1;
    const url = `https://adc.interia.pl/adc/get?url=${host}`;

    const testConfigAPI = isITG ? TEST_CONFIG_API_ITG : TEST_CONFIG_API;
    return testConfig?.isTestConfig ? `${testConfigAPI}&url=${testConfig.testConfigId}&time=${testConfig.testConfigIdTime}` : url;
};

/**
 * Zwaraca dostępne kontenery/sloty (DOM,document), w których można osadzić reklamy.
 *
 * @returns {string[]} Tablicę z id slotów
 */
export const getAvailableSlotsID = (): string[] => {
    const slots: string[] = [];
    document.querySelectorAll('div:not([id^="ad-view"])[id^="ad-"]').forEach((item) => {
        slots.push(item.id);
    });
    document.querySelectorAll('#react_component_article div:not([id^="ad-view"])[class="ad"]').forEach((item) => {
        slots.push(item.id);
    });
    return slots;
};

/**
 * Request do ADC po konfigurację reklam z adSeta.
 * W przypadku gdy nie uda się pobrać konfiguracj z ADC, zwracana jest konfiguracja domyślna.
 *
 * @returns {Promise<IConfig>} Kpnfiguracja reklam
 */
export const adcRequest = async (): Promise<IConfig> => {
    const { Ad } = window.Inpl;
    const requestURL = prepareAdcURL(Ad.host as string, Ad.testConfig || null);

    try {
        const fetchedConfig = await fetchData<IConfig>(requestURL);
        Ad.config = fetchedConfig;
        Ad.UANC instanceof DebugUANC && Ad.UANC.info('Successfully fetched config from ADC');
        return fetchedConfig;
    } catch (error) {
        const defaultConfigName: DEFAULT_CONFIG =
            DEFAULT_CONFIG[window.INPL_UANC_CONFIG_URL as keyof typeof DEFAULT_CONFIG] ??
            DEFAULT_CONFIG[location.host as keyof typeof DEFAULT_CONFIG] ??
            DEFAULT_CONFIG.DEFAULT;

        const defaultConfig = await import('./../../configs/default-configs/' + defaultConfigName);

        console.warn('Failed to fetch config from ADC. Loading the default configuration.', error);
        return defaultConfig.default;
    }
};

/**
 * Ustawia stany serwisów, wywołuje eventy Inpl.Uanc.Config i Inpl.adsLoaded informujące o gotowości wstawki.
 * Przypisuje globalne funkcje w celu uniknięcia błędów po stronie serwisów w przypadku ich wywołania.
 * Inicjuje pluginy. (todo do przetestowania reklamy z Testera / AdConsole)
 * (reklamy wyłączone na podstawie parametru 'noAdvertisement' ustawionego w formatce)
 *
 * @returns {Promise<void>}
 */
export const noAd = async (): Promise<void> => {
    console.log('No advertisement (Reklamy wyłączone z formatki).');
    const { config, event, UANC } = window.Inpl.Ad;

    await embedPlugins();
    event?.custom(UANC_CONFIG_EVENT, config);

    UANC?.setState({ ADOCEANServiceState: STATE_SERVICE.REJECTED });
    UANC?.setState({ DFPServiceState: STATE_SERVICE.REJECTED });
    setGlobalAdServices();

    UANC!.unresolvedServices.length = 0;
    window.Inpl.Ad.services = {
        ...window.Inpl.Ad.services,
        resolved: true,
    };
    event?.custom(ADS_LOADED_EVENT, { services: window.Inpl.Ad.services });
};

/**
 * Przygotowuje obiekty z ustawieniami - settings & exSettings.
 * Metoda przygotowuje konfiguracje: waliduje otrzymane dane z serwisu, ustawia domyślne wartości.
 * Serwis przekazuje informacje ustawiając je bezpośrednio w obiektach Inpl.Ad.settings & Inpl.Ad.exSettings.
 *
 * @returns {void}
 */
export const prepareSettings = async (): Promise<void> => {
    const { settings, exSettings } = window.Inpl.Ad;

    if (exSettings) {
        window.Inpl.Ad.exSettings = {
            ...exSettings,
            adultContent: !!exSettings.adultContent,
            noAdvertisement: !!exSettings.noAdvertisement,
            videoPlayer: !!window.Inpl?.Video,
        };
    }

    if (isEmpty(window.Inpl?.Ad?.exSettings) && settings) {
        const { adultContent, serviceId, urlParams, noAdvertisement } = settings;
        window.Inpl.Ad.settings = {
            ...settings,
            serviceId: isStringType(serviceId) ? parseInt(serviceId, 10) : serviceId,
            urlParams: urlParams ?? {},
            videoPlayer: !!window.Inpl?.Video,
            adultContent: !!adultContent,
            noAdvertisement: !!noAdvertisement,
            isRWD: true,
        };
    }
};

/**
 * Przygotowuje konfigurację na podstawie pobranych ustawień z ADC (lub defaultowych konfiguracji).
 * Aktualizuje konfiguracje o testyAB i ładuje pluginy.
 * Inicjuje core UANCA - osadzanie reklam,serwisów adserwerów.
 *
 * @returns {Promise<void>}
 */
export const loadApp = async (): Promise<void> => {
    const { config, event } = window.Inpl.Ad;

    try {
        if (config?.test) {
            const tests = await import('../../services/TestsAB/ABTestsPrepare');
            await tests.default(config);
        }

        window.Inpl.Ad.config = await configService(config as IConfig);

        await embedPlugins();

        if (config?.slots?.length === 0) {
            document.body.classList.add('uanc-no-ad');
        }

        event!.onReady(() => {
            import(/* webpackChunkName: "core-app" */ '../app').then((module) => module.default());
        });
    } catch (e) {
        console.warn(e);
    }
};
